/**
 * Phase 6e.2 — compute the date range the FX sync should request from
 * the backend `/api/fx/rates`.
 *
 * The range covers:
 *   • earliest FX-contract `commencementDate`, padded 7 calendar days
 *     backwards so that T-1 fallback for the earliest date still has
 *     headroom (TCMB can have multi-day weekend gaps near month
 *     boundaries; 7 days is a safe pad without blowing the backend's
 *     10-year cap),
 *   • through `engagement.auditPeriodEnd` (closing rate + final
 *     historical date both live here).
 *
 * Returns `null` when the engagement has zero FX contracts — the
 * Magic Button can then skip the backend call entirely.
 */

import type { EngagementHistoryEntry } from '@/types';

export interface FxDateRange {
  startDate: string;
  endDate: string;
}

export function computeFxDateRange(
  engagement: EngagementHistoryEntry,
): FxDateRange | null {
  const fxContracts = engagement.contracts.filter(
    (c) => c !== null && c.currency !== engagement.functionalCurrency,
  );
  if (fxContracts.length === 0) return null;

  const commencements: string[] = [];
  for (const c of fxContracts) {
    if (c && typeof c.commencementDate === 'string') {
      commencements.push(c.commencementDate);
    }
  }
  if (commencements.length === 0) return null;

  const earliest = commencements.reduce((min, d) => (d < min ? d : min));
  const padded = subtractCalendarDays(earliest, 7);

  return {
    startDate: padded,
    endDate: engagement.auditPeriodEnd,
  };
}

function subtractCalendarDays(iso: string, days: number): string {
  // Noon UTC avoids DST edge cases when the host server changes clocks.
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}
